/**
 * ContactDealsSection - Seção de Negócios na sidebar do chat
 * Exibe deals vinculados ao contato com filtro de segurança por vendedor
 * 
 * Características:
 * - Card colapsável estilo premium
 * - Avatar do vendedor (SmartAvatar com use3DFallback)
 * - Status badge colorido (Ganho/Perdido/Aberto)
 * - Pipeline + Fase do negócio
 * - Valor formatado em moeda
 * - Lazy loading ativado quando seção é aberta
 * 
 * Versão: 1.0.0
 * RELEASE: 2026-01-14
 */

import { useState, useMemo } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { ChevronRight, TrendingUp, Loader2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useContactDealsForChat, ContactDealForChat } from '@/hooks/useContactDealsForChat';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';

/**
 * Props do componente ContactDealsSection
 */
interface ContactDealsSectionProps {
  /** ID do contato para buscar deals */
  contactId: string;
  /** Callback quando dados são atualizados (para refresh da sidebar) */
  onRefresh?: () => void;
}

/**
 * Formata valor monetário para exibição
 */
function formatCurrency(value: number | null, currency: string | null): string {
  if (value === null) return '—';
  
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `R$ ${value.toLocaleString('pt-BR')}`;
  }
}

/**
 * Retorna configuração de cor do badge de status
 */
function getStatusConfig(status: string): { label: string; className: string } {
  switch (status) {
    case 'won':
      return { 
        label: 'Ganho', 
        className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
      };
    case 'lost':
      return { 
        label: 'Perdido', 
        className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' 
      };
    default:
      return { 
        label: 'Aberto', 
        className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' 
      };
  }
}

/**
 * Card individual de um deal na lista
 */
function DealCard({ 
  deal, 
  onClick 
}: { 
  deal: ContactDealForChat; 
  onClick: () => void;
}) {
  const statusConfig = getStatusConfig(deal.status);
  
  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors text-left overflow-hidden"
    >
      {/* Avatar do vendedor responsável */}
      <SmartAvatar
        src={deal.seller_avatar}
        name={deal.seller_name}
        size="sm"
        defaultFallback="V"
        use3DFallback
        className="shrink-0 mt-0.5"
      />
      
      {/* Conteúdo do deal */}
      <div className="flex-1 min-w-0 space-y-1">
        {/* Linha 1: Título do negócio */}
        <p 
          className="text-sm font-medium text-foreground truncate max-w-[180px]"
          title={deal.title}
        >
          {deal.title}
        </p>
        
        {/* Linha 2: Pipeline > Fase */}
        {(deal.pipeline_name || deal.phase_name) && (
          <p className="text-[10px] text-muted-foreground truncate max-w-[180px]" title={`${deal.pipeline_name || ''}${deal.pipeline_name && deal.phase_name ? ' > ' : ''}${deal.phase_name || ''}`}>
            {deal.pipeline_name}
            {deal.pipeline_name && deal.phase_name && ' > '}
            {deal.phase_name && (
              <span 
                className="font-medium"
                style={{ color: deal.phase_color || undefined }}
              >
                {deal.phase_name}
              </span>
            )}
          </p>
        )}
        
        {/* Linha 3: Valor + Status */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-foreground">
            {formatCurrency(deal.value, deal.currency)}
          </span>
          <Badge 
            variant="secondary" 
            className={cn("text-[10px] px-1.5 py-0 h-4", statusConfig.className)}
          >
            {statusConfig.label}
          </Badge>
        </div>
      </div>
    </button>
  );
}

/**
 * Loading skeleton para a lista de deals
 */
function DealsSkeleton() {
  return (
    <div className="space-y-2 px-4 pb-4">
      {[1, 2].map((i) => (
        <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
          <Skeleton className="h-8 w-8 rounded-full shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Estado vazio quando não há deals
 */
function EmptyState() {
  return (
    <div className="px-4 pb-4">
      <p className="text-xs text-muted-foreground italic text-center py-2">
        Nenhum negócio vinculado a este contato
      </p>
    </div>
  );
}

/**
 * Seção de Negócios na sidebar do chat
 * Card colapsável com lista de deals do contato
 */
export function ContactDealsSection({ contactId, onRefresh }: ContactDealsSectionProps) {
  // Estado do acordeão (inicia aberto para exibir negócios automaticamente)
  const [isOpen, setIsOpen] = useState(true);
  
  // Estado do modal de detalhes do deal
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Estado do modal de criação de novo deal
  const [isNewDealModalOpen, setIsNewDealModalOpen] = useState(false);
  
  // Query com lazy loading - só executa quando acordeão está aberto
  const { data: deals, isLoading, refetch } = useContactDealsForChat(contactId, {
    enabled: isOpen, // Lazy loading
  });
  
  // Lógica de exibição dos deals por prioridade de status:
  // 1. Até 2 negócios em aberto
  // 2. Fallback: 1 negócio ganho
  // 3. Fallback: 1 negócio perdido
  const dealsToShow = useMemo(() => {
    if (!deals || deals.length === 0) return [];
    
    // Prioridade 1: até 2 negócios em aberto
    const openDeals = deals.filter(d => d.status === 'open').slice(0, 2);
    if (openDeals.length > 0) return openDeals;
    
    // Prioridade 2: 1 negócio ganho
    const wonDeals = deals.filter(d => d.status === 'won').slice(0, 1);
    if (wonDeals.length > 0) return wonDeals;
    
    // Prioridade 3: 1 negócio perdido
    const lostDeals = deals.filter(d => d.status === 'lost').slice(0, 1);
    return lostDeals;
  }, [deals]);
  
  // Mostra botão "Novo Negócio" quando:
  // - Não tem nenhum deal
  // - Só tem deals ganhos/perdidos (nenhum aberto)
  const showNewDealButton = useMemo(() => {
    if (!deals) return false;
    if (deals.length === 0) return true;
    
    const hasOpenDeals = deals.some(d => d.status === 'open');
    return !hasOpenDeals; // Mostra se não tem deals abertos
  }, [deals]);
  
  // Mostra ícone "+" no header quando tem 1 ou mais deals
  const showHeaderAddButton = useMemo(() => {
    return deals && deals.length >= 1;
  }, [deals]);
  
  // Handler para abrir modal de detalhes
  const handleOpenDeal = (dealId: string) => {
    setSelectedDealId(dealId);
    setIsModalOpen(true);
  };
  
  // Handler para quando modal fecha (atualiza dados)
  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setSelectedDealId(null);
      // Refetch dos deals após fechar modal (pode ter editado)
      refetch();
      onRefresh?.();
    }
  };
  
  // Handler para criar novo negócio - abre modal com contato pré-selecionado
  const handleNewDeal = () => {
    setIsNewDealModalOpen(true);
  };
  
  // Handler para quando modal de criação fecha
  const handleNewDealModalClose = (open: boolean) => {
    setIsNewDealModalOpen(open);
    if (!open) {
      // Refetch dos deals após criar novo negócio
      refetch();
      onRefresh?.();
    }
  };
  
  return (
    <>
      {/* Card colapsável de Negócios */}
      <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                Negócios
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Badge com contador (só aparece se tem deals e não está carregando) */}
              {isOpen && !isLoading && deals && deals.length > 0 && (
                <Badge variant="secondary" className="rounded-full text-xs">
                  {deals.length}
                </Badge>
              )}
              {/* Botão "+" para novo negócio - aparece quando tem 1+ deals */}
              {!isLoading && showHeaderAddButton && (
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Evita toggle do acordeão
                    handleNewDeal();
                  }}
                  className="p-1 rounded-md hover:bg-muted/50 text-muted-foreground hover:text-primary transition-colors"
                  title="Novo Negócio"
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
              {/* Ícone de loading ou chevron */}
              {isOpen && isLoading ? (
                <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
              ) : (
                <ChevronRight className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform duration-200",
                  isOpen && "rotate-90"
                )} />
              )}
            </div>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            {isLoading ? (
              <DealsSkeleton />
            ) : dealsToShow.length > 0 ? (
              <div className="space-y-2 px-4 pb-4">
                {dealsToShow.map((deal) => (
                  <DealCard
                    key={deal.id}
                    deal={deal}
                    onClick={() => handleOpenDeal(deal.id)}
                  />
                ))}
                
                {/* Botão "Novo Negócio" quando só tem ganhos/perdidos */}
                {showNewDealButton && (
                  <button
                    onClick={handleNewDeal}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-muted-foreground/30 hover:bg-muted/30 hover:border-primary/50 transition-colors text-muted-foreground hover:text-primary"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="text-sm font-medium">Novo Negócio</span>
                  </button>
                )}
              </div>
            ) : (
              // Estado vazio: mostra apenas botão de criar
              <div className="px-4 pb-4">
                <button
                  onClick={handleNewDeal}
                  className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-muted-foreground/30 hover:bg-muted/30 hover:border-primary/50 transition-colors text-muted-foreground hover:text-primary"
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-sm font-medium">Novo Negócio</span>
                </button>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>
      
      {/* Modal de detalhes do deal */}
      <DealDetailsModalPremium
        dealId={selectedDealId}
        open={isModalOpen}
        onOpenChange={handleModalClose}
      />
      
      {/* Modal de criação de novo negócio com contato pré-selecionado */}
      <DealFormDialog
        open={isNewDealModalOpen}
        onOpenChange={handleNewDealModalClose}
        defaultContactId={contactId}
        onSuccess={() => {
          refetch();
          onRefresh?.();
        }}
      />
    </>
  );
}
